import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { defaultStyle } from "components/Modals";
import { createClientCost, updateClientCost } from "api/resources/clientCosts";
import {
  ClientCost,
  ClientCostRequest,
  ServiceFeeType,
} from "api/models/ClientCost";
import { SERVICE_FEE_TYPE_LABELS } from "helpers/serviceFees";

const DATE_FORMAT = "YYYY-MM-DD";
const AMOUNT_PATTERN = /^\d+([.,]\d{1,2})?$/;
const TOTAL_CONTRACT_TYPE = "TOTAL";

type ServiceFeeModalProps = {
  clientId: number;
  clientContractType?: string;
  serviceFee?: ClientCost;
  onSaved: () => void;
  onClose: () => void;
};

const toField = (value: number | null | undefined) =>
  value === null || value === undefined ? "" : String(value);

const toRequestAmount = (value: string) =>
  value.trim() === "" ? undefined : value.trim().replace(",", ".");

const isAmount = (value: string) => AMOUNT_PATTERN.test(value.trim());

const isOptionalAmount = (value: string) =>
  value.trim() === "" || isAmount(value);

const isPercent = (value: string) =>
  isAmount(value) && Number(value.trim().replace(",", ".")) <= 100;

const isMinNotAboveMax = (min: string, max: string) =>
  min.trim() === "" ||
  max.trim() === "" ||
  Number(min.replace(",", ".")) <= Number(max.replace(",", "."));

const ServiceFeeModal = ({
  clientId,
  clientContractType,
  serviceFee,
  onSaved,
  onClose,
}: ServiceFeeModalProps) => {
  const availableFeeTypes = (
    Object.keys(SERVICE_FEE_TYPE_LABELS) as ServiceFeeType[]
  ).filter(
    (feeType) =>
      feeType !== "FORECAST_VOLUME_FEE" ||
      clientContractType !== TOTAL_CONTRACT_TYPE ||
      serviceFee?.costType === feeType,
  );
  const [costType, setCostType] = useState<ServiceFeeType>(
    (serviceFee?.costType as ServiceFeeType) ?? "MONTHLY_FEE",
  );
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs | null>(
    serviceFee ? dayjs(serviceFee.effectiveFrom) : null,
  );
  const [effectiveTo, setEffectiveTo] = useState<Dayjs | null>(
    serviceFee ? dayjs(serviceFee.effectiveTo) : null,
  );
  const [value, setValue] = useState(toField(serviceFee?.value));
  const [consumptionPercent, setConsumptionPercent] = useState(
    toField(serviceFee?.consumptionPercent),
  );
  const [productionPercent, setProductionPercent] = useState(
    toField(serviceFee?.productionPercent),
  );
  const [consumptionMinEur, setConsumptionMinEur] = useState(
    toField(serviceFee?.consumptionMinEur),
  );
  const [consumptionMaxEur, setConsumptionMaxEur] = useState(
    toField(serviceFee?.consumptionMaxEur),
  );
  const [productionMinEur, setProductionMinEur] = useState(
    toField(serviceFee?.productionMinEur),
  );
  const [productionMaxEur, setProductionMaxEur] = useState(
    toField(serviceFee?.productionMaxEur),
  );
  const [error, setError] = useState<string | null>(null);

  const isPercentageFee = costType === "PRICE_PERCENTAGE_FEE";
  const isPeriodValid =
    effectiveFrom?.isValid() &&
    effectiveTo?.isValid() &&
    !effectiveTo.isBefore(effectiveFrom, "day");
  const areFeeFieldsValid = isPercentageFee
    ? isPercent(consumptionPercent) &&
      isPercent(productionPercent) &&
      [
        consumptionMinEur,
        consumptionMaxEur,
        productionMinEur,
        productionMaxEur,
      ].every(isOptionalAmount) &&
      isMinNotAboveMax(consumptionMinEur, consumptionMaxEur) &&
      isMinNotAboveMax(productionMinEur, productionMaxEur)
    : isAmount(value);
  const isComplete = isPeriodValid && areFeeFieldsValid;

  const valueLabel =
    costType === "MONTHLY_FEE" ? "Service fee (€/month)" : "Fee (€/MWh)";

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const period = {
      costType,
      effectiveFrom: effectiveFrom.format(DATE_FORMAT),
      effectiveTo: effectiveTo.format(DATE_FORMAT),
    };
    const data: ClientCostRequest = isPercentageFee
      ? {
          ...period,
          consumptionPercent: toRequestAmount(consumptionPercent),
          productionPercent: toRequestAmount(productionPercent),
          consumptionMinEur: toRequestAmount(consumptionMinEur),
          consumptionMaxEur: toRequestAmount(consumptionMaxEur),
          productionMinEur: toRequestAmount(productionMinEur),
          productionMaxEur: toRequestAmount(productionMaxEur),
        }
      : { ...period, value: toRequestAmount(value) };

    (serviceFee
      ? updateClientCost(clientId, serviceFee.id, data)
      : createClientCost(clientId, data)
    )
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((err) => {
        setError(
          err.response?.data?.message ?? "Failed to save the service fee",
        );
      });
  };

  const amountField = (
    label: string,
    fieldValue: string,
    setFieldValue: (value: string) => void,
    isValid: (value: string) => boolean,
    helperText: string,
    required = false,
  ) => (
    <TextField
      label={label}
      size="small"
      required={required}
      value={fieldValue}
      onChange={(event) => setFieldValue(event.target.value)}
      error={fieldValue !== "" && !isValid(fieldValue)}
      helperText={helperText}
      inputProps={{ inputMode: "decimal" }}
      sx={{ width: "100%" }}
    />
  );

  return (
    <Box sx={defaultStyle}>
      <Typography variant="h5" component="h5">
        {serviceFee ? "Edit service fee" : "Add service fee"}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} sx={{ mt: "1rem", mb: "2rem" }}>
          <Grid item sm={12}>
            <TextField
              select
              label="Fee type"
              size="small"
              value={costType}
              onChange={(event) =>
                setCostType(event.target.value as ServiceFeeType)
              }
              sx={{ width: "100%" }}
            >
              {availableFeeTypes.map((feeType) => (
                <MenuItem key={feeType} value={feeType}>
                  {SERVICE_FEE_TYPE_LABELS[feeType]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item sm={6}>
            <DatePicker
              label="Valid from"
              value={effectiveFrom}
              onChange={(newValue) => setEffectiveFrom(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item sm={6}>
            <DatePicker
              label="Valid to"
              value={effectiveTo}
              minDate={effectiveFrom ?? undefined}
              onChange={(newValue) => setEffectiveTo(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          {isPercentageFee ? (
            <>
              <Grid item sm={6}>
                {amountField(
                  "Consumption (%)",
                  consumptionPercent,
                  setConsumptionPercent,
                  isPercent,
                  "Of the consumption cost per MTU, 0–100",
                  true,
                )}
              </Grid>
              <Grid item sm={6}>
                {amountField(
                  "Production (%)",
                  productionPercent,
                  setProductionPercent,
                  isPercent,
                  "Of the production revenue per MTU, 0–100",
                  true,
                )}
              </Grid>
              <Grid item sm={6}>
                {amountField(
                  "Consumption min (€ per MTU)",
                  consumptionMinEur,
                  setConsumptionMinEur,
                  isAmount,
                  "Optional",
                )}
              </Grid>
              <Grid item sm={6}>
                {amountField(
                  "Production min (€ per MTU)",
                  productionMinEur,
                  setProductionMinEur,
                  isAmount,
                  "Optional",
                )}
              </Grid>
              <Grid item sm={6}>
                {amountField(
                  "Consumption max (€ per MTU)",
                  consumptionMaxEur,
                  setConsumptionMaxEur,
                  isAmount,
                  isMinNotAboveMax(consumptionMinEur, consumptionMaxEur)
                    ? "Optional"
                    : "Must not be below the minimum",
                )}
              </Grid>
              <Grid item sm={6}>
                {amountField(
                  "Production max (€ per MTU)",
                  productionMaxEur,
                  setProductionMaxEur,
                  isAmount,
                  isMinNotAboveMax(productionMinEur, productionMaxEur)
                    ? "Optional"
                    : "Must not be below the minimum",
                )}
              </Grid>
            </>
          ) : (
            <Grid item sm={12}>
              {amountField(
                valueLabel,
                value,
                setValue,
                isAmount,
                "A non-negative number with up to 2 decimals",
                true,
              )}
            </Grid>
          )}
          {error && (
            <Grid item sm={12}>
              <Alert variant="filled" severity="error">
                {error}
              </Alert>
            </Grid>
          )}
          <Grid item sm={12} sx={{ mt: "1rem" }}>
            <Button
              variant="contained"
              type="submit"
              disabled={!isComplete}
              sx={{ mr: "1rem" }}
            >
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ServiceFeeModal;
